import * as React from "react";
import { isNull } from "lodash";
import { IPageViewsResult, useGAPageViews, IGAViews } from "src/share/components/GoogleAnalitycs/hooks/useGAPageViews";
import { useAlertInsights, IAlertInsightsResponse } from "./useAlertInsights";

export interface IAlertInsights {
  clicks: number;
  day: string;
  views: number;
}

export interface IAlertImpressions {
  day: string;
  impressions: number;
}

export interface IAlertStatistics {
  impressions: IAlertImpressions[];
  pageViews: IPageViewsResult[];
  statistics: IAlertInsights[];
  loading: boolean;
}

type returned = [IAlertStatistics];

export const useAlertStatistics = (alertId: string, views: IGAViews, daysAgo: number | null, pagePath: string, error: boolean): returned => {
  const [alertStatistics, setAlertStatistics] = React.useState<IAlertStatistics>({ impressions: [], loading: true, pageViews: [], statistics: [], });
  const [alertInsights] = useAlertInsights(alertId);
  const { pageViews } = useGAPageViews({ daysAgo, pagePath, views });
  React.useEffect(() => {
    if (!isNull(alertInsights) && !isNull(pageViews)) {
      const impressions = getAlertImpressions(alertInsights);
      const statistics = getAlertStatistics(alertInsights, pageViews);
      setAlertStatistics({ impressions, loading: false, pageViews, statistics });
    }
  }, [daysAgo, alertInsights, pageViews]);
  React.useEffect(() => {
    if (error) {
      setAlertStatistics(prev => ({ ...prev, loading: false }));
    }
  }, [error]);
  return [alertStatistics];
}

const getAlertStatistics = (insights: IAlertInsightsResponse[], pageViews: IPageViewsResult[]) => {
  const statistics = insights.map((insight) => ({ clicks: insight.clicks, day: insight.day, views: 0 }));
  return pageViews.reduce((alertStatistics, pageView) => {
    const index = alertStatistics.findIndex(insigh => insigh.day === pageView.date);
    if (alertStatistics[index]) {
      alertStatistics[index].views = pageView.count;
    } else {
      const newStat: IAlertInsights = {
        clicks: 0,
        day: pageView.date,
        views: pageView.count
      };
      alertStatistics = [...alertStatistics, newStat];
    }
    return alertStatistics;
  }, statistics as IAlertInsights[]);
}

const getAlertImpressions = (insights: IAlertInsightsResponse[]): IAlertImpressions[] => {
  return insights.map(insight => ({ impressions: insight.impressions, day: insight.day }));
}
